import { dispatchStandardCartUpdate } from 'util.standard-events'
import { executeJSmodules } from 'util.misc'
import { EVENTS } from 'util.events'
import { buildShopifyRoute } from 'util.shopify-routes'

// Both the cart page and drawer operate on the same Shopify cart.
let sharedCartMutationQueue = Promise.resolve()
let discountBusy = false
let quantityPending = false
let quantityNeedsRecovery = false
const notifyQuantityState = () => document.dispatchEvent(new CustomEvent('cart:quantity-busy'))
// Module lifetime matches the shared cart, including gaps between connected forms.
document.addEventListener('cart:discount-busy', (event) => { discountBusy = event.detail?.busy === true })

/*============================================================================
  CartForm
  - Prevent checkout when terms checkbox exists
  - Listen to quantity changes, rebuild cart (both widget and page)
==============================================================================*/
export default class CartForm {
  constructor(form) {
    this.selectors = {
      products: '[data-products]',
      discounts: '[data-discounts]',
      subTotal: '[data-subtotal]',
      header: '[data-header]',

      locales: '[data-locales]',
      termsCheckbox: '.cart__terms-checkbox',
      checkoutBtn: '.cart__checkout'
    }

    this.classes = {
      btnLoading: 'btn--loading'
    }

    this.config = {
      requiresTerms: false
    }

    if (!form) {
      return
    }

    this.form = form
    this.abortController = new AbortController()
    this.discountPending = discountBusy
    this.cartBuildRequestId = 0
    this.wrapper = form.parentNode
    this.products = form.querySelector(this.selectors.products)
    this.submitBtn = form.querySelector(this.selectors.checkoutBtn)

    this.discounts = form.querySelector(this.selectors.discounts)
    this.subtotal = form.querySelector(this.selectors.subTotal)
    this.header = form.querySelector(this.selectors.header)
    const termsLabel = form.querySelector(this.selectors.termsCheckbox)
    this.termsCheckbox = termsLabel ? termsLabel.querySelector('input[type="checkbox"]') : null
    const localesEl = form.querySelector(this.selectors.locales)
    try {
      this.locales = JSON.parse(localesEl?.textContent || '{}')
    } catch (error) {
      console.warn('Cart localization JSON could not be parsed.', error)
      this.locales = {}
    }

    if (this.termsCheckbox) {
      this.config.requiresTerms = true
    }

    this.boundQuantityChanged = this.quantityChanged.bind(this)
    this.boundOnSubmit = this.onSubmit.bind(this)
    this.boundBuildCart = this.buildCart.bind(this)
    this.boundResetLoadingState = this.resetLoadingState.bind(this)
    this.boundCartUpdated = this.handleCartUpdated.bind(this)

    this.init()
    this.setDiscountBusy()
  }

  init() {
    document.addEventListener('cart:quantity', this.boundQuantityChanged, {
      signal: this.abortController.signal
    })

    document.addEventListener(EVENTS.cartUpdated, this.boundCartUpdated, {
      signal: this.abortController.signal
    })

    this.form.addEventListener('submit', this.boundOnSubmit, {
      signal: this.abortController.signal
    })

    document.addEventListener('cart:discount-busy', (event) => {
      this.discountPending = discountBusy
      this.cartBuildRequestId += 1
      this.setDiscountBusy()
    }, { signal: this.abortController.signal })
    document.addEventListener('cart:quantity-busy', () => {
      this.cartBuildRequestId += 1
      this.setDiscountBusy()
    }, { signal: this.abortController.signal })
    this.form.addEventListener('cart:discount-request', (event) => {
      if (event.target.closest('form') !== this.form) return
      let expired = false
      let timer
      const operation = async () => {
        clearTimeout(timer)
        if (expired) throw new Error('Discount queue wait expired')
        if (quantityNeedsRecovery) {
          const error = new Error('Cart quantity needs confirmation')
          error.requiresCartRecovery = true
          throw error
        }
        if (this.abortController.signal.aborted) throw new DOMException('Cart form destroyed', 'AbortError')
        const cart = await event.detail.operation()
        if (this.abortController.signal.aborted) return { cart, rendered: false }
        const markup = cart.sections?.['cart-ajax']
        const rendered = Boolean(markup && this.cartMarkup(markup, { requireDiscounts: true }))
        if (rendered) document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart, source: this.form } }))
        return { cart, rendered }
      }
      const queued = this.cartMutationQueue.catch(() => {}).then(operation)
      this.cartMutationQueue = queued
      event.detail.promise = Promise.race([queued, new Promise((_, reject) => {
        timer = setTimeout(() => {
          expired = true
          const error = new Error('Previous cart update is still unconfirmed')
          error.requiresCartRecovery = true
          reject(error)
        }, 15000)
      })])
    }, { signal: this.abortController.signal })

    // Dev-friendly way to build the cart
    document.addEventListener('cart:build', this.boundBuildCart, {
      signal: this.abortController.signal
    })

    // Reset loading state on page load/back navigation
    this.resetLoadingState()
    window.addEventListener('pageshow', this.boundResetLoadingState, {
      signal: this.abortController.signal
    })
  }

  destroy() {
    this.abortController?.abort()
  }

  resetLoadingState() {
    if (this.submitBtn) {
      this.submitBtn.classList.remove(this.classes.btnLoading)
    }
  }

  get cartMutationQueue() { return sharedCartMutationQueue }
  set cartMutationQueue(value) { sharedCartMutationQueue = value }

  setDiscountBusy() {
    // Inert includes remove links and native wallet hosts, without replacing
    // their existing disabled state or reaching into payment shadow roots.
    const locked = this.discountPending || quantityPending || quantityNeedsRecovery
    for (const element of this.form.querySelectorAll('[data-products], .cart-recommendations, .cart__checkout-wrapper, .cart__checkout, cart-note, discount-code')) {
      // A discount owns its own controls while it is running.
      const inert = element.matches('discount-code') ? quantityPending || quantityNeedsRecovery : locked
      if (inert && !element.inert) { element.inert = true; element.dataset.discountInert = '' }
      if (!inert && element.hasAttribute('data-discount-inert')) { element.inert = false; delete element.dataset.discountInert }
    }
    if (quantityNeedsRecovery && !this.form.querySelector('[data-cart-quantity-recovery]')) {
      const message = document.createElement('p')
      message.dataset.cartQuantityRecovery = ''
      message.setAttribute('role', 'alert')
      message.textContent = (this.locales.cartQuantityRecovery || 'We couldn’t confirm the updated cart. Open your cart to check before trying again.') + ' '
      const link = document.createElement('a')
      link.href = buildShopifyRoute('cart')
      link.textContent = this.locales.viewCart || 'View cart'
      message.append(link)
      this.form.prepend(message)
    }
  }

  onSubmit(evt) {
    if (this.discountPending || quantityPending || quantityNeedsRecovery) { evt.preventDefault(); return false }

    this.submitBtn?.classList.add(this.classes.btnLoading)

    /*
      Checks for drawer or cart open class on body element
      and then stops the form from being submitted.

      Error is handled in the quantityChanged method

      For Expanse/Fetch/Gem/Vino quick add, if an error is present it is alerted
      through the add to cart fetch request in quick-add.js.
    */

    if (
      (document.documentElement.classList.contains('js-drawer-open') && this.cartItemsUpdated) ||
      (document.documentElement.classList.contains('cart-open') && this.cartItemsUpdated)
    ) {
      this.submitBtn?.classList.remove(this.classes.btnLoading)
      evt.preventDefault()
      return false
    }

    if (this.config.requiresTerms) {
      if (this.termsCheckbox?.checked) {
        // continue to checkout
      } else {
        alert(this.locales.cartTermsConfirmation || 'Please accept the cart terms before checking out.')
        this.submitBtn?.classList.remove(this.classes.btnLoading)
        evt.preventDefault()
        return false
      }
    }
  }

  /*============================================================================
    Query cart page to get markup
  ==============================================================================*/
  _parseProductHTML(text) {
    const html = document.createElement('div')
    html.innerHTML = text

    return {
      items: html.querySelector('.cart__items'),
      discounts: html.querySelector('.cart__discounts'),
      subtotal: html.querySelector('.cart__subtotal'),
      count: html.querySelector('.cart-link__bubble'),
      header: html.querySelector('.cart__header')
    }
  }

  buildCart() {
    const requestId = ++this.cartBuildRequestId

    return this.getCartProductMarkup()
      .then((text) => {
        if (!this.isActiveCartRequest(requestId)) {
          return false
        }

        return this.cartMarkup(text)
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || this.abortController.signal.aborted) {
          return false
        }

        return this.handleCartBuildError(error)
      })
  }

  isActiveCartRequest(requestId) {
    return !this.abortController.signal.aborted && requestId === this.cartBuildRequestId
  }

  cartMarkup(text, { requireDiscounts = false } = {}) {
    const markup = this._parseProductHTML(text)
    const items = markup.items
    if (!this.wrapper || !this.products || !this.subtotal) {
      this.handleCartBuildError(new Error('Cart target markup is missing required nodes.'))
      return false
    }

    if (!items || !markup.subtotal || !markup.header || (requireDiscounts && !markup.discounts)) {
      this.handleCartBuildError(new Error('Cart section markup is missing required nodes.'))
      return false
    }

    // Accepted bundled markup also supersedes a pending startup section read.
    this.cartBuildRequestId += 1

    const header = markup.header.innerHTML
    const count = parseInt(items.dataset.count, 10)
    const subtotal = markup.subtotal.innerHTML

    this.updateCartDiscounts(markup.discounts)

    if (!Number.isNaN(count)) {
      if (count > 0) {
        this.wrapper.classList.remove('is-empty')
      } else {
        this.wrapper.classList.add('is-empty')
      }
    }

    // Append item markup
    this.products.innerHTML = ''
    this.products.append(items)
    const scripts = this.products.querySelectorAll('script[type="module"]')
    executeJSmodules(scripts)

    // Update subtotal
    this.subtotal.innerHTML = subtotal

    // Update header
    if (this.header) this.header.innerHTML = header

    this.setDiscountBusy()
    window.Shopify?.StorefrontExpressButtons?.initialize?.()

    return true
  }

  updateCartDiscounts(markup) {
    if (!this.discounts || !markup) {
      return
    }
    this.discounts.innerHTML = ''
    this.discounts.append(markup)
  }

  handleCartBuildError(error) {
    console.warn('Cart markup refresh skipped.', error)
    this.resetLoadingState()
    return false
  }

  quantityChanged(evt) {
    const [key, qty, el] = evt.detail || []
    if (!this.form.contains(el || evt.target) || this.discountPending || quantityPending || quantityNeedsRecovery) return

    if (!key || typeof qty === 'undefined' || qty === null) {
      return
    }

    // Disable qty selector so multiple clicks can't happen while loading
    if (el) {
      el.classList.add('is-loading')
    }

    quantityPending = true
    notifyQuantityState()
    let expired = false
    let timer
    const update = this.queueCartLinesUpdate({
      id: key,
      quantity: qty,
      // Bundled section rendering
      sections: 'cart-ajax'
    }, () => !expired)
    return Promise.race([update, new Promise((_, reject) => {
      timer = setTimeout(() => {
        expired = true
        reject(new Error('Cart quantity confirmation timed out'))
      }, 15000)
    })])
      .then((state) => {
        if (this.abortController.signal.aborted) { quantityNeedsRecovery = true; return }

        this.cartBuildRequestId += 1
        const cartMarkup = state.sections?.['cart-ajax']
        if (!cartMarkup || !this.cartMarkup(cartMarkup)) {
          quantityNeedsRecovery = true
          return
        }

        document.dispatchEvent(new CustomEvent(EVENTS.cartUpdated, { detail: { cart: state, source: this.form } }))
      })
      .catch((response) => {
        if (response?.name === 'AbortError') return
        // Only a definite HTTP rejection makes another write safe.
        if (!Number.isFinite(response?.status) || response.status < 400 || response.status >= 500) {
          quantityNeedsRecovery = true
          return
        }
        if (this.abortController.signal.aborted) return

        alert(response.description || response.message || 'Cart quantity update failed.')

        if (el) {
          el.classList.remove('is-loading')
          const input = el.querySelector('.element-quantity-selector__input')
          if (input) {
            input.value = input.dataset.initialValue
            el.style.setProperty('--digit-count', `${input.value.toString().length}ch`)
          }
        }
      })
      .finally(() => {
        clearTimeout(timer)
        quantityPending = false
        notifyQuantityState()
      })
  }

  handleCartUpdated(evt) {
    if (evt.detail?.source === this.form) return
    const markup = evt.detail?.cart?.sections?.['cart-ajax']
    if (!markup) return

    // A confirmed mutation supersedes any older section-only read.
    this.cartBuildRequestId += 1
    this.cartMarkup(markup)
  }

  queueCartLinesUpdate(body, isCurrent = () => true) {
    const runUpdate = () => {
      if (!isCurrent() || quantityNeedsRecovery) throw new Error('Cart quantity needs confirmation')
      if (this.abortController.signal.aborted) throw new DOMException('Cart form destroyed', 'AbortError')
      return this.dispatchStandardCartLinesUpdate(body)
    }
    const queuedUpdate = this.cartMutationQueue.catch(() => {}).then(runUpdate)
    this.cartMutationQueue = queuedUpdate
    return queuedUpdate
  }

  dispatchStandardCartLinesUpdate(body) {
    return dispatchStandardCartUpdate('dispatchCartLinesUpdate', {
      target: this.form,
      action: parseInt(body.quantity, 10) === 0 ? 'remove' : 'update',
      context: document.body?.classList.contains('template-cart') ? 'cart' : 'dialog',
      lines: [
        {
          id: body.id,
          quantity: body.quantity
        }
      ],
      operation: () => this.changeItem(body)
    })
  }

  changeItem(body) {
    return fetch(buildShopifyRoute('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }).then(async (response) => {
      // Error bodies belong to the same confirmation deadline as success bodies.
      const data = await response.json().catch((error) => {
        if (response.ok) throw error
        return {}
      })
      if (!response.ok) {
        const error = new Error('Cart quantity update failed.')
        error.status = response.status
        error.description = data.description || data.message
        throw error
      }
      return data
    })
  }

  getCartProductMarkup() {
    const url = buildShopifyRoute('', { section_id: 'cart-ajax' })

    return fetch(url, {
      credentials: 'same-origin',
      method: 'GET',
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) throw response
        return response.text()
      })
  }
}
